<template>
    <div class="fm-sidebar" :style="width ? {width: width + 'px'} : null">
        <div class="fm-sidebar-scroll">
            <div class="fm-sidebar-section">
                <div class="fm-sidebar-title">{{ $t('common.fileManager.categories') }}</div>
                <template v-if="!fm.state._categoriesReady">
                    <div v-for="i in 4" :key="'skel-cat-'+i" class="fm-sidebar-item fm-sidebar-skeleton-item">
                        <span class="fm-skeleton-circle fm-skeleton-circle--sm"></span>
                        <span class="fm-skeleton-text fm-skeleton-text--sidebar"></span>
                    </div>
                </template>
                <template v-else>
                    <div v-for="cat in categories" :key="cat.key"
                         :class="['fm-sidebar-item', {'fm-sidebar-item--active': fm.state.activeCategory === cat.key, 'fm-sidebar-item--drop-target': catDropTarget === cat.key}]"
                         @click="$emit('selectCategory', cat.key)"
                         @dragover.prevent="onCatDragOver($event, cat.key)"
                         @dragleave="onCatDragLeave(cat.key)"
                         @drop.prevent="onCatDrop($event, cat.key)">
                        <span class="fm-sidebar-icon" v-html="cat.icon"></span>
                        <span>{{ $t(cat.label) }}</span>
                        <span class="fm-sidebar-count">{{ fm.categoryCounts.value[cat.key] || 0 }}</span>
                    </div>
                </template>
            </div>

            <div class="fm-sidebar-section">
                <div class="fm-sidebar-title">
                    {{ $t('common.fileManager.folders') }}
                    <div v-if="fm.state._foldersReady" class="fm-sidebar-title-actions">
                        <button v-if="flatParentIds.length" class="fm-sidebar-title-btn"
                                @mousedown.prevent="toggleCollapseAll"
                                v-tooltip="allCollapsed ? $t('common.fileManager.expandAll') : $t('common.fileManager.collapseAll')">
                            <svg width="12" height="12" viewBox="0 0 12 12">
                                <path :d="allCollapsed ? 'M4.5 2L9 6L4.5 10' : 'M2 4.5L6 9L10 4.5'"
                                      stroke="currentColor" stroke-width="1.5" fill="none"
                                      stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </button>
                        <button v-if="!pickerMode" class="fm-sidebar-title-btn" @mousedown.prevent="startNewRootFolder"
                                v-tooltip="$t('common.fileManager.newFolder')">
                            <svg width="12" height="12" viewBox="0 0 12 12">
                                <path d="M6 1v10M1 6h10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                            </svg>
                        </button>
                    </div>
                </div>

                <template v-if="!fm.state._foldersReady">
                    <div v-for="i in 3" :key="'skel-fld-'+i" class="fm-sidebar-item fm-sidebar-skeleton-item">
                        <span class="fm-skeleton-circle fm-skeleton-circle--sm"></span>
                        <span class="fm-skeleton-text fm-skeleton-text--sidebar"></span>
                    </div>
                </template>
                <template v-else>
                    <div v-if="showNewFolder && pendingParentId == null" class="fm-sidebar-inline-form">
                        <input ref="newFolderInput" v-model="newFolderName" :placeholder="$t('common.fileManager.folderName')"
                               @keydown.enter="submitNewFolder" @keydown.esc.stop="cancelNewFolder"
                               @blur="onNewFolderBlur" class="fm-sidebar-inline-input"/>
                    </div>

                    <div v-if="flatFolderList.length === 0 && !showNewFolder" class="fm-sidebar-empty-hint">{{ $t('common.fileManager.noFolders') }}</div>

                    <template v-for="item in flatFolderList" :key="item.id">
                        <div :class="sidebarFolderClass(item)"
                             :style="folderStyle(item)"
                             :data-folder-id="item.id"
                             :data-sidebar-folder-id="item.id"
                             :draggable="!pickerMode"
                             @click="$emit('navigateFolder', item.id)"
                             @contextmenu.prevent="!pickerMode && $emit('folderContext', { event: $event, folder: item })"
                             @dragstart="onFolderDragStart($event, item)"
                             @dragend="onFolderDragEnd"
                             @dragover.prevent="onFolderDragOver($event, item)"
                             @dragleave="onFolderDragLeave"
                             @drop.prevent="onFolderDrop($event, item)"
                             @touchstart.passive="!pickerMode && onFolderLongPressStart($event, item)"
                             @touchend.passive="!pickerMode && onFolderLongPressEnd($event)">
                            <span :class="['fm-sidebar-folder-toggle', {'fm-sidebar-folder-toggle--open': !isCollapsed(item.id), 'fm-sidebar-folder-toggle--empty': !item._hasChildren}]"
                                  @click.stop="toggleCollapse(item.id)">
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="9 18 15 12 9 6"/></svg>
                            </span>
                            <span class="fm-sidebar-folder-icon" :style="{color: item.color}" v-html="folderIconSvg"></span>
                            <input v-if="editingFolderId === item.id"
                                   ref="renameFolderInput"
                                   class="fm-sidebar-inline-input fm-sidebar-inline-input--edit"
                                   v-model="editingFolderName"
                                   @keydown.enter="saveRenameFolder(item)"
                                   @keydown.esc.stop="cancelRenameFolder"
                                   @blur="saveRenameFolder(item)"
                                   @click.stop/>
                            <span v-else class="fm-sidebar-folder-name">{{ item.nameKey ? $t(item.nameKey) : item.name }}</span>
                            <span :class="['fm-sidebar-folder-count', {'fm-sidebar-folder-count--empty': !folderCount(item) && !item._hasChildren}]">{{ folderCount(item) }}</span>
                        </div>

                        <div v-if="showNewFolder && pendingParentId === item.id"
                             class="fm-sidebar-inline-form"
                             :style="{paddingLeft: (8 + (item.depth + 1) * 16) + 'px'}">
                            <input ref="newFolderInput" v-model="newFolderName" :placeholder="$t('common.fileManager.folderName')"
                                   @keydown.enter="submitNewFolder" @keydown.esc.stop="cancelNewFolder"
                                   @blur="onNewFolderBlur" class="fm-sidebar-inline-input"/>
                        </div>
                    </template>
                </template>
            </div>
        </div>

        <div v-if="!pickerMode" class="fm-sidebar-trash-wrap">
            <div class="fm-sidebar-trash"
                 :class="{'fm-sidebar-trash--active': fm.state.activeCategory === 'TRASH', 'fm-sidebar-trash--drag-over': dragOverTrash, 'fm-sidebar-trash--eaten': trashEating}"
                 @click="$emit('selectCategory', 'TRASH')"
                 @dragover.prevent="onTrashDragOver"
                 @dragleave="dragOverTrash = false"
                 @drop.prevent="onTrashDrop">
                <span class="fm-sidebar-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg></span>
                <span>{{ $t('common.fileManager.trash') }}</span>
                <span v-if="fm.state._trashCount > 0" class="fm-sidebar-trash-count">{{ fm.state._trashCount }}</span>
                <div v-if="trashEating" class="fm-trash-paper">
                    <svg width="16" height="20" viewBox="0 0 20 24" fill="none">
                        <path d="M2 0 L18 0 L20 2 L18 4 L20 8 L17 10 L19 14 L16 16 L18 20 L14 22 L16 24 L4 24 L6 22 L2 20 L4 16 L1 14 L3 10 L0 8 L2 4 L0 2 Z" class="fm-trash-paper-path"/>
                    </svg>
                </div>
            </div>
        </div>

        <div class="fm-sidebar-stats">
            <template v-if="fm.state.storageQuota">
                <div class="fm-sidebar-quota-text">
                    <span :class="'fm-quota-level--' + quotaLevel">%{{ fm.state.storageQuota.usage_percent }}</span>
                    <span class="fm-sidebar-quota-detail">{{ fm.state.storageQuota.used_text }} / {{ fm.state.storageQuota.total_text }}</span>
                </div>
                <div class="fm-sidebar-quota-bar">
                    <div :class="['fm-sidebar-quota-fill', 'fm-quota-level--' + quotaLevel]" :style="{width: fm.state.storageQuota.usage_percent + '%'}"></div>
                </div>
            </template>
            <div class="fm-sidebar-stats-text">
                {{ $t('common.fileManager.statsTemplate', {files: fm.storageStats.value.totalFiles, folders: fm.storageStats.value.totalFolders}) }}
            </div>
        </div>
    </div>
</template>

<script>
import fileManagerService from './fileManagerService'

export default {
    name: 'FileManagerSidebar',
    props: {
        width: { type: Number, default: null },
        pickerMode: { type: Boolean, default: false },
    },
    emits: ['selectCategory', 'navigateFolder', 'dividerStart', 'folderContext', 'folderReorder', 'folderDrop', 'renameFolder', 'trashDrop', 'depthLimit', 'categoryDrop'],
    data() {
        return {
            collapsedIds: new Set(),
            dropTargetId: null,
            dropIndicator: null,
            dropTargetDepth: 0,
            draggingFolderId: null,
            showNewFolder: false,
            newFolderName: '',
            pendingParentId: null,
            skipBlur: false,
            editingFolderId: null,
            editingFolderName: '',
            dragOverTrash: false,
            trashEating: false,
            catDropTarget: null,
        }
    },
    computed: {
        fm() { return fileManagerService },
        quotaLevel() {
            const p = this.fm.state.storageQuota?.usage_percent ?? 0
            if (p >= 85) return 'danger'
            if (p >= 60) return 'warn'
            return 'ok'
        },
        folderIconSvg() {
            return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>'
        },
        flatFolderList() {
            const list = []
            const walk = (folders, depth) => {
                for (const f of folders) {
                    const hasChildren = f.children && f.children.length > 0
                    list.push({ ...f, depth, _hasChildren: hasChildren, parent_id: f.parentId })
                    if (hasChildren && !this.collapsedIds.has(f.id)) {
                        walk(f.children, depth + 1)
                    }
                }
            }
            walk(fileManagerService.folderTree.value, 0)
            return list
        },
        flatParentIds() {
            const ids = []
            const collect = (items) => {
                for (const item of items) {
                    if (item.children && item.children.length > 0) {
                        ids.push(item.id)
                        collect(item.children)
                    }
                }
            }
            collect(fileManagerService.folderTree.value)
            return ids
        },
        allCollapsed() {
            if (!this.flatParentIds.length) return false
            return this.flatParentIds.every(id => this.collapsedIds.has(id))
        },
        categories() {
            const iconMap = {
                ALL: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>',
                MODULE_NOTE: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="13" y2="17"/></svg>',
                MODULE_VEHICLE_WORK_ORDER: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9L18.4 7.6c-.3-.8-1.1-1.6-2-1.6H7.6c-.9 0-1.7.8-2 1.6L3.5 11.1C2.7 11.3 2 12.1 2 13v3c0 .6.4 1 1 1h2"/><circle cx="7" cy="17" r="2"/><circle cx="17" cy="17" r="2"/></svg>',
                MODULE_MUSIC: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>',
                MODULE_MUSIC_PLAYLIST: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>',
                UNATTACHED: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>',
            }
            let cats = fileManagerService.sidebarCategories.value
            return cats.map(key => ({
                key,
                label: fileManagerService.CATEGORY_LABELS[key],
                icon: iconMap[key],
            }))
        },
    },
    watch: {
        'fm.state.currentFolderId'(id) {
            if (!id) return
            const folders = fileManagerService.state.folders
            let f = folders.find(f => f.id === id)
            while (f && f.parentId) {
                const next = new Set(this.collapsedIds)
                next.delete(f.parentId)
                this.collapsedIds = next
                f = folders.find(p => p.id === f.parentId)
            }
        },
    },
    beforeUnmount() {
        clearTimeout(this._longPressTimer)
        this.cleanupDragGhost()
    },
    methods: {
        folderCount(item) {
            const own = item.fileCount || 0
            if (!item._hasChildren || !this.collapsedIds.has(item.id)) return own
            const sumChildren = (parentId) => {
                let total = 0
                for (const f of fileManagerService.state.folders) {
                    if (f.parentId === parentId && !f.moduleType && !f.entityId) {
                        total += (f.fileCount || 0) + sumChildren(f.id)
                    }
                }
                return total
            }
            return own + sumChildren(item.id)
        },
        isCollapsed(id) {
            return this.collapsedIds.has(id)
        },
        toggleCollapse(id) {
            const next = new Set(this.collapsedIds)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            this.collapsedIds = next
        },
        toggleCollapseAll() {
            if (this.allCollapsed) {
                this.collapsedIds = new Set()
            } else {
                this.collapsedIds = new Set(this.flatParentIds)
            }
        },

        startNewRootFolder() {
            this.skipBlur = true
            this.pendingParentId = null
            this.newFolderName = ''
            this.showNewFolder = true
            this.$nextTick(() => {
                this.skipBlur = false
                this._focusInput()
            })
        },
        startNewSubFolder(parentId) {
            const next = new Set(this.collapsedIds)
            next.delete(parentId)
            this.collapsedIds = next

            this.skipBlur = true
            this.pendingParentId = parentId
            this.newFolderName = ''
            this.showNewFolder = true
            this.$nextTick(() => {
                this.skipBlur = false
                this._focusInput()
            })
        },
        _focusInput() {
            const ref = this.$refs.newFolderInput
            const el = Array.isArray(ref) ? ref[0] : ref
            el?.focus()
        },
        async submitNewFolder() {
            const name = this.newFolderName.trim()
            if (!name) { this.cancelNewFolder(); return }
            const parentId = this.pendingParentId
            this.showNewFolder = false
            this.newFolderName = ''
            this.pendingParentId = null

            const folder = fileManagerService.createFolder(name, parentId)
            if (!folder) return
            await fileManagerService.saveFolderToBackend(folder)
        },
        cancelNewFolder() {
            this.showNewFolder = false
            this.newFolderName = ''
            this.pendingParentId = null
        },
        onNewFolderBlur() {
            if (this.skipBlur) return
            this.$nextTick(() => this.cancelNewFolder())
        },

        startRenameFolder(folder) {
            this.editingFolderId = folder.id
            this.editingFolderName = folder.name
            this.$nextTick(() => {
                const ref = this.$refs.renameFolderInput
                const el = Array.isArray(ref) ? ref[0] : ref
                if (el) { el.focus(); el.select() }
            })
        },
        saveRenameFolder(folder) {
            if (!this.editingFolderId) return
            const name = this.editingFolderName.trim()
            const uid = folder.uid
            this.editingFolderId = null
            this.editingFolderName = ''
            if (name && name !== folder.name) {
                this.$emit('renameFolder', { uid, name })
            }
        },
        cancelRenameFolder() {
            this.editingFolderId = null
            this.editingFolderName = ''
        },

        sidebarFolderClass(folder) {
            return ['fm-sidebar-folder-item', {
                'fm-sidebar-folder-item--active': fileManagerService.state.currentFolderId === folder.id,
                'fm-sidebar-folder-item--drop-target': this.dropTargetId === folder.id && !this.dropIndicator,
                'fm-sidebar-folder-item--drop-before': this.dropIndicator === 'before-' + folder.id,
                'fm-sidebar-folder-item--drop-after': this.dropIndicator === 'after-' + folder.id,
            }]
        },
        folderStyle(folder) {
            const style = { paddingLeft: (8 + folder.depth * 16) + 'px' }
            const hasIndicator = this.dropIndicator === 'before-' + folder.id || this.dropIndicator === 'after-' + folder.id
            if (hasIndicator) {
                style['--drop-indent'] = (8 + this.dropTargetDepth * 16) + 'px'
            }
            return style
        },

        onFolderDragStart(e, folder) {
            if (this.pickerMode || folder._pendingCreate) { e.preventDefault(); return }
            this.draggingFolderId = folder.id
            fileManagerService.state._draggingSidebarFolderId = folder.id
            e.dataTransfer.setData('fm-folder-id', folder.uid)
            e.dataTransfer.effectAllowed = 'move'

            if ('ontouchstart' in window) {
                const el = e.currentTarget
                const ghost = el.cloneNode(true)
                const rect = el.getBoundingClientRect()
                ghost.style.cssText = 'position:fixed;top:-1000px;left:-1000px;width:' + rect.width + 'px;background:var(--fm-sidebar-bg,#f2f2f7);opacity:0.9;border-radius:6px;box-shadow:0 4px 16px rgba(0,0,0,0.15);pointer-events:none;'
                document.body.appendChild(ghost)
                e.dataTransfer.setDragImage(ghost, e.clientX - rect.left, e.clientY - rect.top)
                this._dragGhost = ghost
            }
        },
        onFolderDragOver(e, folder) {
            if (this.pickerMode) return
            if (e.dataTransfer.types.includes('application/fm-items')) {
                const ids = fileManagerService.state.selectedIds
                const blocked = ids.some(uid => {
                    const f = fileManagerService.state.folders.find(fl => fl.uid === uid)
                    return f && (fileManagerService.isDescendantOf(folder.id, f.id) || f.id === folder.id)
                })
                if (blocked) return
                e.dataTransfer.dropEffect = 'move'
                this.dropTargetId = folder.id
                this.dropIndicator = null
                return
            }

            if (!this.draggingFolderId) return
            if (this.draggingFolderId === folder.id) return
            if (this.isDescendantOf(folder.id, this.draggingFolderId)) return

            e.dataTransfer.dropEffect = 'move'
            const rect = e.currentTarget.getBoundingClientRect()
            const y = e.clientY - rect.top
            const threshold = rect.height * 0.3

            if (y < threshold || y > rect.height - threshold) {
                let targetDepth = folder.depth

                if (y > rect.height - threshold && folder.depth > 0) {
                    const idx = this.flatFolderList.findIndex(f => f.id === folder.id)
                    const next = idx >= 0 ? this.flatFolderList[idx + 1] : null
                    const minDepth = next ? Math.min(folder.depth, next.depth) : 0
                    const maxDepth = folder.depth

                    if (minDepth < maxDepth) {
                        const sectionEl = e.currentTarget.closest('.fm-sidebar-section')
                        const sectionWidth = sectionEl ? sectionEl.clientWidth : 200
                        const sectionLeft = sectionEl ? sectionEl.getBoundingClientRect().left : rect.left
                        const relX = e.clientX - sectionLeft
                        const range = maxDepth - minDepth + 1
                        const zoneWidth = sectionWidth / range
                        targetDepth = minDepth + Math.floor(relX / zoneWidth)
                        targetDepth = Math.max(minDepth, Math.min(targetDepth, maxDepth))
                    }
                }

                this.dropTargetDepth = targetDepth
                this.dropIndicator = (y < threshold ? 'before-' : 'after-') + folder.id
                this.dropTargetId = null
            } else {
                this.dropIndicator = null
                this.dropTargetDepth = 0
                this.dropTargetId = folder.id
            }
        },
        onFolderDragLeave() {
            this.dropTargetId = null
            this.dropIndicator = null
        },
        onFolderDrop(e, folder) {
            if (this.pickerMode) return
            const indicator = this.dropIndicator
            const targetDepth = this.dropTargetDepth
            this.dropTargetId = null
            this.dropIndicator = null
            this.dropTargetDepth = 0

            const fmItems = e.dataTransfer.getData('application/fm-items')
            if (fmItems) {
                try {
                    const uids = JSON.parse(fmItems)
                    if (uids.includes(folder.uid)) return
                    const hasFolders = uids.some(uid => fileManagerService.state.folders.find(f => f.uid === uid))
                    if (hasFolders) {
                        const blocked = uids.some(uid => {
                            const f = fileManagerService.state.folders.find(fl => fl.uid === uid)
                            return f && (fileManagerService.isDescendantOf(folder.id, f.id) || f.id === folder.id)
                        })
                        if (blocked) {
                            this.draggingFolderId = null
                            this.cleanupDragGhost()
                            return
                        }
                        if (!fileManagerService.canCreateSubfolder(folder.id)) {
                            this.$emit('depthLimit', 'move')
                            this.draggingFolderId = null
                            this.cleanupDragGhost()
                            return
                        }
                    }
                    fileManagerService.moveItems(uids, folder.id)
                } catch { /* empty */ }
                this.draggingFolderId = null
                this.cleanupDragGhost()
                return
            }

            const folderUid = e.dataTransfer.getData('fm-folder-id')
            const draggedFolder = folderUid ? this.fm.state.folders.find(f => f.uid === folderUid) : null
            if (!draggedFolder || draggedFolder.id === folder.id) {
                this.draggingFolderId = null
                this.cleanupDragGhost()
                return
            }

            const draggedId = draggedFolder.id

            if (this.isDescendantOf(folder.id, draggedId)) {
                this.draggingFolderId = null
                this.cleanupDragGhost()
                return
            }

            const subtreeDepth = this.getSubtreeDepth(draggedId)

            if (indicator && (indicator.startsWith('before-') || indicator.startsWith('after-'))) {
                const position = indicator.startsWith('before-') ? 'before' : 'after'
                let resolvedTarget = folder
                while (resolvedTarget.depth > targetDepth) {
                    const parent = this.flatFolderList.find(f => f.id === resolvedTarget.parent_id)
                    if (!parent) break
                    resolvedTarget = parent
                }
                const siblings = this.getSiblings(resolvedTarget)
                const targetIndex = siblings.findIndex(s => s.id === resolvedTarget.id)
                const insertIndex = position === 'after' ? targetIndex + 1 : targetIndex
                const draggedFolder = this.flatFolderList.find(c => c.id === draggedId)
                const newParentId = resolvedTarget.parent_id || null

                if (draggedFolder && (draggedFolder.parent_id || null) !== newParentId) {
                    if (resolvedTarget.depth + subtreeDepth >= fileManagerService.MAX_FOLDER_DEPTH) {
                        this.draggingFolderId = null
                        this.cleanupDragGhost()
                        this.$emit('depthLimit', 'move')
                        return
                    }
                }

                const reordered = siblings.filter(s => s.id !== draggedId)
                reordered.splice(Math.min(insertIndex, reordered.length), 0, { id: draggedId })
                const updates = reordered.map((item, idx) => ({ id: item.id, sort: idx, parent_id: newParentId }))
                const draggedUpdate = updates.find(u => u.id === draggedId)
                if (draggedUpdate && draggedFolder && (draggedFolder.parent_id || null) !== newParentId) {
                    draggedUpdate.parentChanged = true
                }

                this.$emit('folderReorder', updates)
            } else {
                if (folder.depth + 1 + subtreeDepth >= fileManagerService.MAX_FOLDER_DEPTH) {
                    this.draggingFolderId = null
                    this.cleanupDragGhost()
                    this.$emit('depthLimit', 'move')
                    return
                }
                this.$emit('folderDrop', { folderId: draggedId, parentId: folder.id })
            }

            this.draggingFolderId = null
            this.cleanupDragGhost()
        },
        onFolderDragEnd() {
            if (this._longPressReady) {
                const dx = Math.abs(event.clientX - this._lpStartX)
                const dy = Math.abs(event.clientY - this._lpStartY)
                if (dx < 40 && dy < 40) {
                    this._longPressReady = false
                    this.$emit('folderContext', {
                        event: { clientX: this._lpStartX, clientY: this._lpStartY, preventDefault() {} },
                        folder: this._lpFolder
                    })
                }
            }
            this._longPressReady = false
            clearTimeout(this._longPressTimer)
            this._longPressTimer = null
            this.draggingFolderId = null
            fileManagerService.state._draggingSidebarFolderId = null
            this.dropTargetId = null
            this.dropIndicator = null
            this.cleanupDragGhost()
        },

        onFolderLongPressStart(event, folder) {
            const touch = event.touches[0]
            this._lpStartX = touch.clientX
            this._lpStartY = touch.clientY
            this._longPressReady = false
            this._lpFolder = folder
            this._longPressTimer = setTimeout(() => {
                this._longPressReady = true
                this._longPressTimer = null
            }, 600)
        },
        onFolderLongPressEnd(event) {
            clearTimeout(this._longPressTimer)
            this._longPressTimer = null
            if (this._longPressReady) {
                const touch = event.changedTouches[0]
                const dx = Math.abs(touch.clientX - this._lpStartX)
                const dy = Math.abs(touch.clientY - this._lpStartY)
                if (dx < 40 && dy < 40) {
                    this._longPressReady = false
                    this.$emit('folderContext', {
                        event: { clientX: this._lpStartX, clientY: this._lpStartY, preventDefault() {} },
                        folder: this._lpFolder
                    })
                    return
                }
            }
            this._longPressReady = false
        },

        onCatDragOver(e, catKey) {
            if (catKey !== 'ALL' && catKey !== 'UNATTACHED') return
            const t = e.dataTransfer.types
            const ids = fileManagerService.state.selectedIds
            if (catKey === 'ALL') {
                if (!t.includes('fm-folder-id') && !t.includes('application/fm-items')) return
                if (!t.includes('fm-folder-id') && !ids.some(uid => uid.startsWith('folder_'))) return
            } else {
                if (t.includes('fm-folder-id') && !t.includes('application/fm-items')) return
                if (t.includes('application/fm-items')) {
                    if (!ids.some(uid => !uid.startsWith('folder_'))) return
                }
            }
            e.dataTransfer.dropEffect = 'move'
            this.catDropTarget = catKey
        },
        onCatDragLeave(catKey) {
            if (this.catDropTarget === catKey) this.catDropTarget = null
        },
        onCatDrop(e, catKey) {
            this.catDropTarget = null
            if (catKey !== 'ALL' && catKey !== 'UNATTACHED') return

            let ids = []
            const fmItems = e.dataTransfer.getData('application/fm-items')
            if (fmItems) {
                try { ids = JSON.parse(fmItems) } catch { /* empty */ }
            }
            const folderUid = e.dataTransfer.getData('fm-folder-id')
            if (folderUid && !ids.length) {
                ids = [folderUid]
            }
            if (!ids.length) return

            if (catKey === 'ALL') {
                ids = ids.filter(uid => uid.startsWith('folder_'))
            } else if (catKey === 'UNATTACHED') {
                ids = ids.filter(uid => !uid.startsWith('folder_'))
            }
            if (!ids.length) return
            this.$emit('categoryDrop', { ids, category: catKey })
        },

        onTrashDragOver(e) {
            const t = e.dataTransfer.types
            if (t.includes('application/fm-items') || t.includes('fm-folder-id')) {
                e.dataTransfer.dropEffect = 'move'
                this.dragOverTrash = true
            }
        },
        onTrashDrop(e) {
            this.dragOverTrash = false

            let ids = []
            const fmItems = e.dataTransfer.getData('application/fm-items')
            if (fmItems) {
                try { ids = JSON.parse(fmItems) } catch { /* empty */ }
            }
            const folderUid = e.dataTransfer.getData('fm-folder-id')
            if (folderUid && !ids.length) {
                ids = [folderUid]
            }
            if (!ids.length) return

            this.trashEating = true
            setTimeout(() => {
                this.trashEating = false
                this.$emit('trashDrop', ids)
            }, 600)
        },

        cleanupDragGhost() {
            if (this._dragGhost) {
                this._dragGhost.remove()
                this._dragGhost = null
            }
        },
        isDescendantOf(folderId, ancestorId) {
            const tree = fileManagerService.folderTree.value
            const find = (items) => {
                for (const item of items) {
                    if (item.id === ancestorId) return hasDescendant(item, folderId)
                    if (item.children && item.children.length) {
                        const r = find(item.children)
                        if (r !== null) return r
                    }
                }
                return null
            }
            const hasDescendant = (node, targetId) => {
                if (!node.children) return false
                for (const child of node.children) {
                    if (child.id === targetId) return true
                    if (hasDescendant(child, targetId)) return true
                }
                return false
            }
            return find(tree) === true
        },
        getSubtreeDepth(folderId) {
            const tree = fileManagerService.folderTree.value
            const calcDepth = (node) => {
                if (!node.children || !node.children.length) return 0
                let max = 0
                for (const child of node.children) max = Math.max(max, 1 + calcDepth(child))
                return max
            }
            const find = (items) => {
                for (const item of items) {
                    if (item.id === folderId) return calcDepth(item)
                    if (item.children && item.children.length) {
                        const r = find(item.children)
                        if (r !== null) return r
                    }
                }
                return null
            }
            return find(tree) ?? 0
        },
        getSiblings(folder) {
            const parentId = folder.parent_id || null
            return this.flatFolderList.filter(c => (c.parent_id || null) === parentId)
        },
    },
}
</script>
